import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../../../common/components/Icon/Icon";

interface CartItemActionsProps {
  onMoveToWishlist?: () => void;
  onRemove?: () => void;
  onSaveForLater?: () => void;
  onToggleCompare?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isInCompare?: boolean;
  isMoving?: boolean;
  isRemoving?: boolean;
  isSaving?: boolean;
  itemName: string;
  context?: "page" | "panel";
  placement?: "top" | "bottom";
}

const CartItemActions: React.FC<CartItemActionsProps> = ({
  onMoveToWishlist,
  onRemove,
  onSaveForLater,
  onToggleCompare,
  isMoving = false,
  isRemoving = false,
  isSaving = false,
  isInCompare = false,
  itemName,
  context = "page",
  placement,
}) => {
  const { t } = useTranslation();
  const isLoading = isMoving || isRemoving || isSaving;

  // Use BEM naming convention for the component
  const baseClass = "cart-item-actions";
  const containerClass = `${baseClass}__container ${baseClass}__container--${context}`;

  // Generate modifier class for placement if provided
  const placementClass = placement ? `${baseClass}--${placement}` : "";

  return (
    <div
      className={`${baseClass} ${baseClass}--${context} ${placementClass}`.trim()}
    >
      <div className={containerClass}>
        {/* Save For Later Button (Only if handler provided) */}
        {onSaveForLater && (
          <button
            onClick={onSaveForLater}
            disabled={isLoading}
            className={`${baseClass}__button ${baseClass}__button--save`}
          >
            <Icon iconName="archive-outlined" className="icon--stroke-thick" />
            <span>
              {isSaving
                ? t("cartItemActions.saving", "Saving...")
                : t("cartItemActions.saveForLater", "Save for Later")}
            </span>
          </button>
        )}

        {/* Move to Wishlist Button (Only if handler provided) */}
        {onMoveToWishlist && (
          <button
            onClick={onMoveToWishlist}
            disabled={isLoading}
            className={`${baseClass}__button ${baseClass}__button--wishlist`}
          >
            <Icon iconName="heart-outline" className="icon--stroke-thick" />
            <span>
              {isMoving
                ? t("cartItemActions.moving", "Moving...")
                : t("cartItemActions.moveToWishlist", "Move to Wishlist")}
            </span>
          </button>
        )}

        {/* Compare Button (Only if handler provided) */}
        {onToggleCompare && (
          <button
            onClick={onToggleCompare}
            disabled={isLoading}
            className={`${baseClass}__button ${baseClass}__button--compare ${
              isInCompare ? "is-active" : ""
            }`}
            aria-pressed={isInCompare}
          >
            <Icon iconName="arrows-right-left" className="icon--stroke-thick" />
            <span>
              {isInCompare
                ? t("cartItemActions.removeFromCompare", "Remove from Compare")
                : t("cartItemActions.compare", "Compare")}
            </span>
          </button>
        )}

        {/* Remove Button (Only if handler provided) */}
        {onRemove && (
          <button
            onClick={onRemove}
            disabled={isLoading}
            className={`${baseClass}__button ${baseClass}__button--remove`}
            aria-label={`${t(
              "cartItemActions.removeItem",
              "Remove this item"
            )}: ${itemName}`}
          >
            <Icon iconName="x-mark" className="icon--stroke-thickest" />
            <span>
              {isRemoving
                ? t("cartItemActions.removing", "Removing...")
                : t("cartItemActions.removeItem", "Remove")}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default CartItemActions;
